// ===========================================================================
// The parent <-> frame protocol (contract D0), the part with no DOM in it.
//
// An activity page runs in a sandboxed iframe with an opaque origin: it has
// no storage, no network, and no view of the parent. The ONLY thing it ever
// hears is what the parent posts to it, and the only thing it can do is post
// back. Both sides tag every message { lumi: 1, type, ... } and accept a
// message only from the one window they expect. This module is the rule for
// "one of ours", written once so the parent and every page agree, and kept
// free of the DOM so it can be tested under Node. ASCII only.
// ===========================================================================

/** The tag on every message. Not a version: the shape is additive. */
export const LUMI_TAG = 1;

/** What the parent may say to a frame. */
export const PARENT_TYPES = Object.freeze(['init', 'state', 'event']);
/** What a frame may say to the parent. */
export const FRAME_TYPES = Object.freeze(['ready', 'set', 'event', 'frame', 'stop']);

/** A tagged message. */
export function frameMessage(type, payload = {}) {
  return { lumi: LUMI_TAG, type, ...payload };
}

/**
 * The message inside a MessageEvent if it is one of ours from the window we
 * expect, else null.
 *
 * The source check is the one that matters: any page open in the browser can
 * post to any window it can reach, and a frame that trusted the shape alone
 * could be driven by a tab it has never heard of. `expectedOrigin`, when
 * given, is a second lock for the frame's side -- the parent's origin, which
 * the frame knows from its own URL even though its own origin is opaque.
 *
 * @param {{ source?: unknown, data?: unknown, origin?: string }|null|undefined} e
 * @param {unknown} expectedSource   the window the message must come from
 * @param {ReadonlyArray<string>} allowedTypes
 * @param {string} [expectedOrigin]
 * @returns {({ lumi: 1, type: string } & Record<string, unknown>)|null}
 */
export function acceptMessage(e, expectedSource, allowedTypes, expectedOrigin) {
  if (!e || !expectedSource || e.source !== expectedSource) return null;
  if (expectedOrigin !== undefined && e.origin !== expectedOrigin) return null;
  const d = e.data;
  if (!d || typeof d !== 'object' || Array.isArray(d)) return null;
  if (d.lumi !== LUMI_TAG) return null;
  if (typeof d.type !== 'string' || !allowedTypes.includes(d.type)) return null;
  return d;
}

/**
 * The name a page shows for a participant: their handle, "You" for the
 * viewer, and "Someone" for a person with no handle -- never a peer id,
 * which is an opaque token and reads as a mistake on screen.
 * @param {string|null|undefined} peerId
 * @param {{ selfId?: string|null, participants?: ReadonlyArray<{ peerId: string, handle?: string|null }> }} me
 */
export function displayName(peerId, me) {
  if (peerId && me && peerId === me.selfId) return 'You';
  const p = me?.participants?.find((x) => x && x.peerId === peerId);
  const handle = p && typeof p.handle === 'string' ? p.handle.trim() : '';
  return handle || 'Someone';
}

export default { LUMI_TAG, PARENT_TYPES, FRAME_TYPES, frameMessage, acceptMessage, displayName };
